import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';

import { first, map, share } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';
import _ from 'lodash';

import { NavigationModel } from '../navigation.model';
import { navigationElements } from '../navigation-elements';
import { UrlHelper } from '../../common/helpers/url.helper';
import { NavigationService } from '../navigation.service';
import { DynamicLinkCreatorService } from '../../common/services/dynamic-link-creator.service';
import { DirtyFormGuardConnectorService } from '../../common/services/dirty-form-guard-connector.service';
import { Maybe } from '../../common/utils/utils';
import { DeploymentActivityContextService, DeploymentActivityIndicator } from '../../common/services/deployment-activity-context.service';

/**
 * This component contains a list of tab items which is part of the navigation bar.
 */
@Component({
    selector: 'adm4-tabs',
    template: `
      <button class="global-search-trigger" [class.hidden]="!(showSearchOnProjectTab | async)" (click)="searchClicked()">
        <mat-icon>search</mat-icon>
        <span>Search...</span>
      </button>
      <adm4-tab-item [title]='"Configuration"'
        [path]='linkCreatorService.configurationLink | async'
        [active]="isSelected(tabs.projects,(selectedTab|async))"
      ></adm4-tab-item>
      <adm4-tab-item [title]='"Infrastructure"'
        [path]='linkCreatorService.inventoriesLink | async'
        [active]="isSelected(tabs.infrastructure,(selectedTab|async))"
      ></adm4-tab-item>
      <adm4-tab-item [title]='"Resources"'
        [path]='linkCreatorService.resourcesLink | async'
        [active]="isSelected(tabs.userManagement,(selectedTab|async))"
      ></adm4-tab-item>
      @if ((deploymentActivity | async) || {hasActivity: false}; as activity) {
        <button class='tab-element nav-deploy-btn focus-nevis-green' (click)='deploy()'
          [class.activity-current-scope]="activity.inCurrentScope"
          [ngbTooltip]="deploymentActivityTooltip" [placement]="'bottom-right'"
          [disableTooltip]="!activity.hasActivity">
          <span class='tab-title'>Deploy</span>
          @if (activity.hasActivity) {
            <!-- the spinner is repeated for the 2 sizes, because the size can not be set from css -->
            <!-- the width of the icon is set to a parent div, because the spinner has inline size set -->
            <div class="deploy-icon show-large">
              <mat-progress-spinner class="spinner-white" [diameter]="18" [mode]="'indeterminate'"></mat-progress-spinner>
            </div>
            <div class="deploy-icon show-small">
              <mat-progress-spinner class="spinner-white" [diameter]="16" [mode]="'indeterminate'"></mat-progress-spinner>
            </div>
          } @else {
            <mat-icon class="deploy-icon">play_circle_filled</mat-icon>
          }
        </button>
        <ng-template #deploymentActivityTooltip>
          <adm4-tabs-deployment-activity [activities]="activity.activities" />
        </ng-template>
      }
      `,
    styleUrls: ['tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TabsComponent {
  private router = inject(Router);
  linkCreatorService = inject(DynamicLinkCreatorService);
  formGuardConnectorService = inject(DirtyFormGuardConnectorService);
  private navigationService = inject(NavigationService);

  public selectedTab: Observable<NavigationModel | undefined>;
  public tabs: Record<string, NavigationModel> = navigationElements;
  public showSearchOnProjectTab: Observable<boolean>;
  public deploymentActivity: Observable<DeploymentActivityIndicator>;

  constructor() {
    const navigationService = this.navigationService;
    const deploymentActivityContext = inject(DeploymentActivityContextService);

    this.selectedTab = merge(
        navigationService.urlAfterRedirects$,
        // adding the current path when the first router event happens, because sometimes the actual navigation is already finished
        this.router.events.pipe(
            first(),
            map(() => this.router.url),
        ),
    ).pipe(
        map((path: string) => this.getSelectedTabFromUrl(path)),
        share(),
    );

    this.showSearchOnProjectTab = this.selectedTab.pipe(
        map((nav: Maybe<NavigationModel>): boolean => navigationElements.projects === nav || navigationElements.infrastructure === nav),
    );

    this.deploymentActivity = deploymentActivityContext.deploymentActivityIndicator$;
  }

  public searchClicked() {
    this.navigationService.navigateToGlobalSearch();
  }

  getSelectedTabFromUrl(url: string): NavigationModel | undefined {
    const selectedTabPath = decodeURI(UrlHelper.getHeadOfUrl(url));
    return _.values(navigationElements).find(tab => tab.path === selectedTabPath) || undefined;
  }

  isSelected(tab: NavigationModel, selectedTab: NavigationModel) {
    if (!_.isNil(selectedTab)) {
      return tab === selectedTab;
    }
    return tab === this.getSelectedTabFromUrl(this.router.url);
  }

  deploy() {
    this.formGuardConnectorService.doIfConfirmed(() => this.navigationService.navigateToDeploymentWizard());
  }

}
